// frontend/src/pages/cargo/PaymentStep.js
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../services/api';
import { setStepData } from '../../store/slices/cargoSlice';

// =========================================================
// FIELD RESOLVER — single source of truth
// =========================================================
function firstNonEmpty(...values) {
    for (const v of values) {
        if (v !== null && v !== undefined && String(v).trim() !== '') {
            return String(v).trim();
        }
    }
    return '';
}

// =========================================================
// CONSTANTS
// =========================================================
const POLL_PAYMENT_MS = 5_000;
const POLL_APPROVAL_MS = 10_000;
const IFRAME_LOAD_TIMEOUT_MS = 30_000;

// Trusted origins for postMessage validation
const TRUSTED_ORIGINS = [
    'pesaflow.com',
    'ecitizen.go.ke',
    'insurance.ecitizen.go.ke',
    'mayfair.co.ke',
];

function isTrustedOrigin(origin) {
    if (!origin) return false;
    return TRUSTED_ORIGINS.some((domain) => origin.includes(domain));
}

// =========================================================
// MAIN COMPONENT
// =========================================================
export default function PaymentStep({ acceptance, onComplete, coverFormData: propCoverFormData, onBack }) {
    const dispatch = useDispatch();
    const { flowData } = useSelector((state) => state.cargo);

    // ---------- UI State ----------
    const [status, setStatus] = useState('idle');
    const [iframeUrl, setIframeUrl] = useState(null);
    const [transactionId, setTransactionId] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [approvalStatus, setApprovalStatus] = useState(null);
    const [checkingApproval, setCheckingApproval] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [iframeTimedOut, setIframeTimedOut] = useState(false);
    const [prefillSent, setPrefillSent] = useState(false);

    // ---------- Refs ----------
    const iframeRef = useRef(null);
    const paymentPollRef = useRef(null);
    const approvalPollRef = useRef(null);
    const iframeTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const prefillTimersRef = useRef([]);

    // ---------- Derived IDs ----------
    const acceptanceId = acceptance?.db_id || acceptance?.id || null;
    const quoteNumber =
        acceptance?.body?.quoteNumber || acceptance?.quote_number || 'N/A';
    const totalCharges =
        Number(acceptance?.body?.totalCharges || acceptance?.totalCharges || 0);

    // ---------- Merged form data ----------
    const coverFormData = propCoverFormData || flowData?.coverFormData || {};

    // ---------- All resolved fields (memoized) ----------
    const fields = useMemo(() => {
        const consignee = flowData?.idfData?.body?.consignee?.[0] || {};
        const complimentaries = flowData?.idfData?.body?.complimentaries || {};

        return {
            idfNumber: firstNonEmpty(
                coverFormData?.idf_number,
                coverFormData?.idfNumber,
                acceptance?.body?.sadId,
                acceptance?.body?.idf_number,
                acceptance?.idf_number,
                acceptance?.idfNumber,
                flowData?.idfData?.body?.idf_number,
                flowData?.idfData?.body?.idfNumber,
                flowData?.coverData?.idf_number,
                flowData?.coverData?.idfNumber,
                flowData?.idf_number,
                flowData?.idfNumber,
                acceptance?.body?.idf?.idf_number,
                acceptance?.body?.idf?.idfNumber,
            ),
            vesselName: firstNonEmpty(
                coverFormData?.vessel_name,
                flowData?.vessel_name,
                acceptance?.body?.vessel_name,
                acceptance?.vessel_name,
            ),
            loadingAt: firstNonEmpty(
                coverFormData?.loading_at,
                flowData?.loading_at,
                acceptance?.body?.loading_at,
                acceptance?.loading_at,
            ),
            portOfDischarge: firstNonEmpty(
                coverFormData?.port_of_discharge,
                flowData?.port_of_discharge,
                acceptance?.body?.port_of_discharge,
                acceptance?.port_of_discharge,
            ),
            openCoverNumber: firstNonEmpty(
                coverFormData?.open_cover_number,
                flowData?.open_cover_number,
                acceptance?.body?.open_cover_number,
                acceptance?.open_cover_number,
            ),
            voyageFrom: firstNonEmpty(
                coverFormData?.voyage_from_country,
                flowData?.voyage_from_country,
                acceptance?.body?.voyage_from,
                acceptance?.voyage_from,
            ),
            voyageTo:
                firstNonEmpty(
                    coverFormData?.voyage_to_country,
                    flowData?.voyage_to_country,
                    acceptance?.body?.voyage_to,
                    acceptance?.voyage_to,
                ) || 'Kenya',
            modeOfTransport:
                firstNonEmpty(
                    coverFormData?.mode_of_transport,
                    flowData?.mode_of_transport,
                    acceptance?.body?.mode_of_transport,
                    acceptance?.mode_of_transport,
                ) || 'Sea',
            contact: {
                email: firstNonEmpty(
                    coverFormData?.consignee_email,
                    flowData?.consignee_email,
                    consignee?.email,
                    complimentaries?.email,
                    acceptance?.body?.email,
                ),
                phone_number: firstNonEmpty(
                    coverFormData?.consignee_phone,
                    flowData?.consignee_phone,
                    consignee?.phone_number,
                    complimentaries?.phone_number,
                    acceptance?.body?.phone_number,
                ),
                postal_address_line_1: firstNonEmpty(
                    coverFormData?.consignee_address,
                    flowData?.consignee_address,
                    consignee?.postal_address,
                    complimentaries?.postal_address,
                ),
                postal_code: firstNonEmpty(
                    coverFormData?.consignee_postal_code,
                    flowData?.consignee_postal_code,
                    consignee?.postal_code,
                    complimentaries?.postal_code,
                ),
                city: firstNonEmpty(
                    coverFormData?.consignee_city,
                    flowData?.consignee_city,
                    consignee?.city,
                    complimentaries?.city,
                ),
                county: firstNonEmpty(
                    coverFormData?.consignee_county,
                    flowData?.consignee_county,
                    consignee?.county,
                    complimentaries?.county,
                ),
                country_of_origin: firstNonEmpty(
                    coverFormData?.consignee_country_of_origin,
                    flowData?.consignee_country_of_origin,
                    flowData?.idfData?.body?.origin_country,
                    consignee?.country_of_origin,
                ),
            },
        };
    }, [acceptance, coverFormData, flowData]);

    // =========================================================
    // PREFILL PAYLOAD (memoized — stable identity)
    // =========================================================
    const prefillPayload = useMemo(
        () => ({
            type: 'PREFILL_DATA',
            event: 'prefill_form',
            data: {
                idf_number: fields.idfNumber,
                vessel_name: fields.vesselName,
                loading_at: fields.loadingAt,
                port_of_discharge: fields.portOfDischarge,
                open_cover_number: fields.openCoverNumber,
                voyage_from: fields.voyageFrom,
                voyage_to: fields.voyageTo,
                mode_of_transport: fields.modeOfTransport,
                contact: fields.contact,
            },
        }),
        [fields]
    );

    // =========================================================
    // POST PREFILL TO IFRAME
    // =========================================================
    const sendPrefillToIframe = useCallback(() => {
        try {
            const win = iframeRef.current?.contentWindow;
            if (!win) return false;
            // Post to specific origin if known, otherwise '*'
            // (MCI iframe doesn't send us its origin reliably, so use '*')
            win.postMessage(prefillPayload, '*');
            setPrefillSent(true);
            return true;
        } catch (err) {
            console.error('[PaymentStep] Failed to send prefill:', err.message);
            return false;
        }
    }, [prefillPayload]);

    // =========================================================
    // CLEANUP HELPERS
    // =========================================================
    const stopAllPolling = useCallback(() => {
        if (paymentPollRef.current) {
            clearInterval(paymentPollRef.current);
            paymentPollRef.current = null;
        }
        if (approvalPollRef.current) {
            clearInterval(approvalPollRef.current);
            approvalPollRef.current = null;
        }
        if (iframeTimeoutRef.current) {
            clearTimeout(iframeTimeoutRef.current);
            iframeTimeoutRef.current = null;
        }
        prefillTimersRef.current.forEach((t) => clearTimeout(t));
        prefillTimersRef.current = [];
    }, []);

    // =========================================================
    // MOUNT / UNMOUNT
    // =========================================================
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            stopAllPolling();
        };
    }, [stopAllPolling]);

    // =========================================================
    // APPROVAL CHECK (on mount)
    // =========================================================
    useEffect(() => {
        if (acceptanceId && status === 'idle') {
            checkApprovalStatus();
        }
        // eslint-disable-next-line
    }, [acceptanceId]);

    // =========================================================
    // PREFILL WHEN IFRAME READY
    // =========================================================
    useEffect(() => {
        if (status !== 'ready' || !iframeUrl || prefillSent) return;
        if (!iframeRef.current) return;

        // Send on load
        const handleLoad = () => {
            setIframeLoaded(true);
            if (iframeTimeoutRef.current) {
                clearTimeout(iframeTimeoutRef.current);
                iframeTimeoutRef.current = null;
            }
            setTimeout(() => sendPrefillToIframe(), 300);
        };

        const iframe = iframeRef.current;
        iframe.addEventListener('load', handleLoad);

        // Also try sending immediately in case already loaded
        const t1 = setTimeout(() => sendPrefillToIframe(), 500);
        const t2 = setTimeout(() => sendPrefillToIframe(), 1500);
        prefillTimersRef.current.push(t1, t2);

        // Timeout if iframe never loads
        iframeTimeoutRef.current = setTimeout(() => {
            if (!iframeLoaded && isMountedRef.current) {
                setIframeTimedOut(true);
            }
        }, IFRAME_LOAD_TIMEOUT_MS);

        return () => {
            iframe.removeEventListener('load', handleLoad);
            clearTimeout(t1);
            clearTimeout(t2);
        };
        // eslint-disable-next-line
    }, [status, iframeUrl, sendPrefillToIframe, prefillSent]);

    // =========================================================
    // IFRAME MESSAGE LISTENER (single, stable)
    // =========================================================
    useEffect(() => {
        const handleMessage = (event) => {
            // Only accept messages from trusted origins
            if (!isTrustedOrigin(event.origin)) return;
            if (!isMountedRef.current) return;

            let data = event.data;
            if (typeof data === 'string') {
                try { data = JSON.parse(data); } catch { return; }
            }
            if (!data || typeof data !== 'object') return;

            // Prefill requested by iframe
            if (data.type === 'REQUEST_PREFILL' || data.event === 'request_prefill') {
                sendPrefillToIframe();
                return;
            }

            // Payment complete signals
            const isPaymentComplete =
                data.event === 'payment_complete' ||
                data.type === 'payment_success' ||
                data.status === 'completed' ||
                data.status === 'paid';

            if (isPaymentComplete) {
                handlePaymentComplete();
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
        // eslint-disable-next-line
    }, [sendPrefillToIframe]);

    // =========================================================
    // APPROVAL STATUS CHECK
    // =========================================================
    const checkApprovalStatus = useCallback(async () => {
        setCheckingApproval(true);
        setErrorMsg('');

        try {
            // Try quote number first (fast path)
            if (quoteNumber && quoteNumber !== 'N/A') {
                try {
                    const { data } = await api.get(`/cargo/quotations/status/${quoteNumber}`);
                    if (!isMountedRef.current) return;

                    if (data.is_approved) {
                        setApprovalStatus('approved');
                        setCheckingApproval(false);
                        return generateMCIToken();
                    }
                    if (data.is_rejected) {
                        setApprovalStatus('rejected');
                        setErrorMsg('Quotation was rejected. Please contact support.');
                        setStatus('failed');
                        setCheckingApproval(false);
                        return;
                    }
                } catch (err) {
                    // Fall through to acceptance ID check
                }
            }

            // Fallback: acceptance_id
            const { data } = await api.get(`/cargo/approval/status/${acceptanceId}`);
            if (!isMountedRef.current) return;

            if (data.is_approved) {
                setApprovalStatus('approved');
                generateMCIToken();
            } else if (data.is_rejected) {
                setApprovalStatus('rejected');
                setErrorMsg('Quotation was rejected. Please contact support.');
                setStatus('failed');
            } else {
                setApprovalStatus('pending');
                setStatus('pending_approval');
                startApprovalPolling();
            }
        } catch (err) {
            if (!isMountedRef.current) return;
            console.error('[PaymentStep] Approval check failed:', err.message);
            setErrorMsg('Could not check approval status. Please try again.');
            setStatus('failed');
        } finally {
            if (isMountedRef.current) setCheckingApproval(false);
        }
    }, [quoteNumber, acceptanceId]); // eslint-disable-line

    // =========================================================
    // APPROVAL POLLING
    // =========================================================
    const startApprovalPolling = useCallback(() => {
        if (approvalPollRef.current) clearInterval(approvalPollRef.current);

        approvalPollRef.current = setInterval(async () => {
            if (!isMountedRef.current) return;
            try {
                const { data } = await api.get(`/cargo/approval/status/${acceptanceId}`);
                if (!isMountedRef.current) return;

                if (data.is_approved) {
                    setApprovalStatus('approved');
                    setErrorMsg('');
                    setStatus('idle');
                    stopAllPolling();
                    generateMCIToken();
                } else if (data.is_rejected) {
                    setApprovalStatus('rejected');
                    setErrorMsg('Quotation was rejected. Please contact support.');
                    setStatus('failed');
                    stopAllPolling();
                }
            } catch (err) {
                console.warn('[PaymentStep] Approval poll error:', err.message);
            }
        }, POLL_APPROVAL_MS);
    }, [acceptanceId, stopAllPolling]); // eslint-disable-line

    // =========================================================
    // MCI TOKEN GENERATION
    // =========================================================
    const generateMCIToken = useCallback(async () => {
        setStatus('processing');
        setErrorMsg('');

        if (!acceptanceId) {
            setErrorMsg('No acceptance ID found. Please go back and try again.');
            setStatus('failed');
            return;
        }
        if (!fields.idfNumber) {
            setErrorMsg('IDF number is required for payment.');
            setStatus('failed');
            return;
        }

        try {
            const payload = {
                acceptance_id: Number(acceptanceId),
                idf_number: fields.idfNumber,
                service_type: 'marine_cargo',
                contact: fields.contact,
                vessel_name: fields.vesselName,
                loading_at: fields.loadingAt,
                port_of_discharge: fields.portOfDischarge,
                open_cover_number: fields.openCoverNumber,
                voyage_from: fields.voyageFrom,
                voyage_to: fields.voyageTo,
                mode_of_transport: fields.modeOfTransport,
            };

            const { data: tokenData } = await api.post('/cargo/mci/token', payload);
            if (!isMountedRef.current) return;

            if (!tokenData?.url) {
                throw new Error('Payment gateway did not return a URL');
            }

            setIframeUrl(tokenData.url);
            setTransactionId(tokenData.transaction_id || tokenData.token);
            setPrefillSent(false);
            setIframeLoaded(false);
            setIframeTimedOut(false);
            setStatus('ready');

            startPaymentPolling(tokenData.transaction_id || tokenData.token);
        } catch (err) {
            if (!isMountedRef.current) return;
            console.error('[PaymentStep] MCI token error:', err.message);
            setStatus('failed');
            setErrorMsg(
                err.response?.data?.error ||
                err.message ||
                'Failed to initialize payment gateway'
            );
        }
    }, [acceptanceId, fields]); // eslint-disable-line

    // =========================================================
    // PAYMENT POLLING
    // =========================================================
    const startPaymentPolling = useCallback(
        (txId) => {
            if (paymentPollRef.current) clearInterval(paymentPollRef.current);

            paymentPollRef.current = setInterval(async () => {
                if (!isMountedRef.current) return;
                try {
                    const { data } = await api.get('/cargo/mci/payment/status', {
                        params: { transaction_id: txId, idf_number: fields.idfNumber },
                    });
                    if (!isMountedRef.current) return;

                    const paid = data.payment_status === 'paid' || data.status === 'paid';
                    const failed =
                        data.payment_status === 'failed' || data.status === 'failed';

                    if (paid) {
                        stopAllPolling();
                        handlePaymentSuccess(txId, data);
                    } else if (failed) {
                        stopAllPolling();
                        setStatus('failed');
                        setErrorMsg('Payment failed. Please try again.');
                    }
                } catch (err) {
                    console.warn('[PaymentStep] Payment poll error:', err.message);
                }
            }, POLL_PAYMENT_MS);
        },
        [fields.idfNumber, stopAllPolling] // eslint-disable-line
    );

    // =========================================================
    // SUCCESS HANDLER (single entry point)
    // =========================================================
    const handlePaymentSuccess = useCallback(
        (txId, paymentData) => {
            if (!isMountedRef.current) return;

            setStatus('paid');
            dispatch(
                setStepData({
                    payment: {
                        transaction_id: txId,
                        status: 'paid',
                        payment_data: paymentData,
                    },
                })
            );

            setTimeout(() => {
                if (!isMountedRef.current) return;
                onComplete({
                    transaction_id: txId,
                    status: 'paid',
                    payment_data: paymentData,
                });
            }, 1500);
        },
        [dispatch, onComplete]
    );

    const handlePaymentComplete = useCallback(async () => {
        if (!transactionId) return;
        try {
            const { data } = await api.get('/cargo/mci/payment/status', {
                params: { transaction_id: transactionId, idf_number: fields.idfNumber },
            });
            const paid = data.payment_status === 'paid' || data.status === 'paid';
            if (paid) {
                stopAllPolling();
                handlePaymentSuccess(transactionId, data);
            }
        } catch (err) {
            console.warn('[PaymentStep] Final check failed:', err.message);
        }
    }, [transactionId, fields.idfNumber, stopAllPolling, handlePaymentSuccess]);

    // =========================================================
    // RETRY / CANCEL
    // =========================================================
    const handleRetry = () => {
        stopAllPolling();
        setStatus('idle');
        setErrorMsg('');
        setIframeUrl(null);
        setIframeLoaded(false);
        setIframeTimedOut(false);
        setPrefillSent(false);
        setTransactionId(null);
        checkApprovalStatus();
    };

    const handleCancelPayment = () => {
        stopAllPolling();
        setStatus('idle');
        setIframeUrl(null);
        setIframeLoaded(false);
        setIframeTimedOut(false);
        setPrefillSent(false);
        if (onBack) onBack();
    };

    // =========================================================
    // RENDER
    // =========================================================
    return (
        <div className="max-w-5xl mx-auto animate-fadeIn space-y-6">

            {/* ========== HEADER ========== */}
            <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-xl shadow-[#3a663f]/25 mb-2">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h2 className="text-3xl font-extrabold text-gray-800 dark:text-white tracking-tight">
                    Secure Payment
                </h2>
                <p className="text-gray-500 dark:text-slate-400 max-w-md mx-auto">
                    Complete your payment to activate your insurance policy
                </p>
                {fields.idfNumber && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-700 rounded-full">
                        <span className="text-xs text-gray-500 dark:text-slate-400">IDF:</span>
                        <span className="text-xs font-mono font-semibold text-gray-800 dark:text-white">
                            {fields.idfNumber}
                        </span>
                    </div>
                )}
            </div>

            {/* ========== MAIN CARD ========== */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">

                {/* Card Header */}
                <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-[#3a663f]/5 to-transparent dark:from-[#3a663f]/10">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#3a663f]/10 dark:bg-[#3a663f]/20 flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-800 dark:text-white">
                                    Ecitizen Payment Gateway
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    Secure transaction via MICL
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Pill icon="lock" tone="green" label="SSL Secured" />
                            <Pill icon="shield" tone="blue" label="Verified" />
                        </div>
                    </div>
                </div>

                {/* Card Body */}
                <div className="p-6 space-y-6">

                    {/* Quote Summary */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-slate-700/30 rounded-xl border border-gray-100 dark:border-slate-600">
                        <div>
                            <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-slate-500">
                                Quote Number
                            </p>
                            <p className="text-sm font-semibold text-gray-800 dark:text-white mt-1">
                                {quoteNumber}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-slate-500">
                                Total Premium
                            </p>
                            <p className="text-xl font-bold text-[#3a663f] dark:text-green-400 mt-1 tabular-nums">
                                KES {totalCharges.toLocaleString()}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-slate-500">
                                Status
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                    Ready for Payment
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Trip details */}
                    <div>
                        <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-3">
                            Trip Details
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <TripTile label="Vessel" value={fields.vesselName} />
                            <TripTile label="Loading At" value={fields.loadingAt} />
                            <TripTile label="Discharge" value={fields.portOfDischarge} />
                            <TripTile label="Mode" value={fields.modeOfTransport} />
                        </div>
                    </div>

                    {/* ========== STATUS STATES ========== */}

                    {status === 'pending_approval' && (
                        <div className="text-center py-8 space-y-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30">
                                <svg className="w-8 h-8 text-amber-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h4 className="text-lg font-bold text-amber-600 dark:text-amber-400">
                                Waiting for Approval
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-slate-400">
                                {errorMsg || 'An underwriter is reviewing your quotation.'}
                            </p>
                            <button
                                onClick={checkApprovalStatus}
                                disabled={checkingApproval}
                                className="mt-2 inline-flex items-center gap-2 bg-[#3a663f] text-white font-semibold py-2 px-6 rounded-lg hover:bg-[#2d5232] transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#3a663f]/50"
                            >
                                {checkingApproval ? 'Checking...' : 'Check Status'}
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {status === 'processing' && (
                        <div className="text-center py-8 space-y-4">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#3a663f]/10">
                                <svg className="animate-spin h-8 w-8 text-[#3a663f]" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                            </div>
                            <h4 className="text-lg font-bold text-gray-800 dark:text-white">
                                Preparing Payment Gateway
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-slate-400">
                                Please wait while we redirect you to a secure payment page
                            </p>
                            {transactionId && (
                                <p className="text-xs text-gray-400 font-mono">
                                    Ref: {transactionId}
                                </p>
                            )}
                        </div>
                    )}

                    {status === 'ready' && iframeUrl && (
                        <div className="space-y-4">
                            {/* Action bar */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-slate-700/30 rounded-lg border border-gray-100 dark:border-slate-600">
                                <div className="flex gap-2 w-full sm:w-auto">
                                    <button
                                        onClick={sendPrefillToIframe}
                                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-[#3a663f] hover:bg-[#2d5232] text-white text-sm font-medium rounded-lg transition-all shadow-sm hover:shadow-md active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#3a663f]/50"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Auto-fill Form
                                    </button>
                                    <button
                                        onClick={() => window.open(iframeUrl, '_blank', 'noopener,noreferrer')}
                                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg border border-gray-300 dark:border-slate-600 transition-all shadow-sm hover:shadow-md active:scale-95 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        Open in New Tab
                                    </button>
                                </div>
                            </div>

                            {/* Iframe */}
                            <div className="relative border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-lg">
                                {!iframeLoaded && !iframeTimedOut && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/95 dark:bg-slate-900/95 z-10 transition-opacity duration-500">
                                        <div className="text-center max-w-sm px-6">
                                            <svg className="animate-spin h-14 w-14 text-[#3a663f] mx-auto" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            <p className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Loading Payment Gateway
                                            </p>
                                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                                                Please wait while we redirect you...
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {iframeTimedOut && !iframeLoaded && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/95 dark:bg-slate-900/95 z-10">
                                        <div className="text-center max-w-sm px-6 space-y-3">
                                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30">
                                                <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Taking longer than expected
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                                The payment gateway is slow to respond. Try opening it in a new tab.
                                            </p>
                                            <button
                                                onClick={() => window.open(iframeUrl, '_blank', 'noopener,noreferrer')}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#3a663f] hover:bg-[#2d5232] text-white text-sm font-medium rounded-lg transition-all"
                                            >
                                                Open in New Tab
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <iframe
                                    ref={iframeRef}
                                    src={iframeUrl}
                                    width="100%"
                                    height="750px"
                                    frameBorder="0"
                                    title="Marine Cargo Insurance Payment"
                                    className="w-full"
                                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                                    allow="payment"
                                />
                            </div>

                            {/* Footer */}
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                                <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-slate-500">
                                    <span className="flex items-center gap-1.5">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        256-bit SSL
                                    </span>
                                    <span className="w-px h-3 bg-gray-300 dark:bg-slate-600 hidden sm:block" />
                                    <span>Powered by <strong className="text-gray-600 dark:text-gray-300">MICL</strong></span>
                                </div>
                                <button
                                    onClick={handleCancelPayment}
                                    className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300 rounded px-2 py-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Cancel Payment
                                </button>
                            </div>
                        </div>
                    )}

                    {status === 'paid' && (
                        <div className="text-center py-10 space-y-5 bg-gradient-to-b from-green-50 to-white dark:from-green-900/10 dark:to-transparent rounded-xl">
                            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 shadow-lg">
                                <svg className="w-12 h-12 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    Payment Successful
                                </h4>
                                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                                    Your insurance policy has been activated
                                </p>
                            </div>
                            {transactionId && (
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-700 rounded-lg">
                                    <span className="text-xs text-gray-500 dark:text-slate-400">Txn ID:</span>
                                    <span className="text-xs font-mono font-semibold text-gray-800 dark:text-white">
                                        {transactionId}
                                    </span>
                                </div>
                            )}
                            <button
                                onClick={() => {
                                    stopAllPolling();
                                    onComplete({ transaction_id: transactionId, status: 'paid' });
                                }}
                                className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5232] text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#3a663f]/50 focus:ring-offset-2"
                            >
                                Continue to Certificate
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {status === 'failed' && (
                        <div className="text-center py-10 space-y-5 bg-gradient-to-b from-red-50 to-white dark:from-red-900/10 dark:to-transparent rounded-xl">
                            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-100 dark:bg-red-900/30 shadow-lg">
                                <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="text-2xl font-bold text-red-600 dark:text-red-400">
                                    Payment Failed
                                </h4>
                                <p className="text-sm text-red-500 dark:text-red-400 mt-1">
                                    {errorMsg}
                                </p>
                            </div>
                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={handleCancelPayment}
                                    className="inline-flex items-center gap-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 font-semibold py-2.5 px-6 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRetry}
                                    className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5232] text-white font-semibold py-2.5 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#3a663f]/50 focus:ring-offset-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Try Again
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// =========================================================
// SUB-COMPONENTS
// =========================================================
function Pill({ icon, tone, label }) {
    const tones = {
        green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
        blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    };
    const paths = {
        lock: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
        shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border ${tones[tone]}`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={paths[icon]} />
            </svg>
            {label}
        </span>
    );
}

function TripTile({ label, value }) {
    return (
        <div className="bg-white dark:bg-slate-700/50 rounded-lg p-3 border border-gray-100 dark:border-slate-600 shadow-sm hover:shadow-md transition-shadow">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500">
                {label}
            </p>
            <p className="text-sm font-medium text-gray-800 dark:text-white truncate" title={value || '—'}>
                {value || '—'}
            </p>
        </div>
    );
}